import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiSend, KbError } from './client'

/** 把后端 Problem Details 广播到统一错误条，保留 trace_id 供运营定位。 */
export function reportError(error: unknown) {
  if (error instanceof KbError) {
    window.dispatchEvent(new CustomEvent('kb:error', { detail: error }))
    return
  }
  window.dispatchEvent(
    new CustomEvent('kb:error', {
      detail: { code: 'INTERNAL_ERROR', message: String(error), traceId: '-', status: 0, retryable: true, degrade: '—' },
    }),
  )
}

export function useQuery<T>(loader: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef(loader)
  const requestSequence = useRef(0)
  const inFlight = useRef<Promise<void> | null>(null)
  ref.current = loader

  const load = useCallback(async (quiet: boolean) => {
    const sequence = ++requestSequence.current
    if (!quiet) setLoading(true)
    setError(null)
    try {
      const result = await ref.current()
      if (sequence === requestSequence.current) setData(result)
    } catch (caught) {
      if (sequence !== requestSequence.current) return
      setError(caught instanceof Error ? caught.message : String(caught))
      if (!quiet) reportError(caught)
    } finally {
      if (sequence === requestSequence.current) setLoading(false)
    }
  }, [])
  const refresh = useCallback(() => {
    if (inFlight.current) return inFlight.current
    const request = load(true)
    inFlight.current = request
    void request.finally(() => { if (inFlight.current === request) inFlight.current = null })
    return request
  }, [load])
  const reload = useCallback(async () => {
    // Explicit reload after a mutation must observe the committed change.
    const generation = requestSequence.current
    if (inFlight.current) await inFlight.current
    if (generation !== requestSequence.current) return
    const request = load(false)
    inFlight.current = request
    try { await request } finally { if (inFlight.current === request) inFlight.current = null }
  }, [load])

  useEffect(() => {
    requestSequence.current++
    inFlight.current = null
    void reload()
    return () => { requestSequence.current++; inFlight.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { data, loading, error, reload, refresh, setData }
}

export function useAction() {
  const [pending, setPending] = useState(false)

  const run = useCallback(async (action: () => Promise<void>) => {
    setPending(true)
    try {
      await action()
    } catch (caught) {
      reportError(caught)
    } finally {
      setPending(false)
    }
  }, [])

  return { pending, run }
}

export { apiGet, apiSend }
